/* eslint-disable */


import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, FlaskConical, FileText, Calendar, Building, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useConfirm } from "../components/ConfirmDialog";
import { api } from "../lib/api";
import { sortByName } from "../lib/utils";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";

const RESULT_OPTIONS = [
  { value: "CONFORME", label: "Conforme", color: "bg-[#E8EEE9] text-[#1F4E3D] border-[#A8C5B0]" },
  { value: "NON_CONFORME", label: "Non conforme", color: "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" },
  { value: "EN_ATTENTE", label: "En attente", color: "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" },
];

function resultBadge(result) {
  const opt = RESULT_OPTIONS.find((o) => o.value === result) || RESULT_OPTIONS[2];
  return <Badge variant="outline" className={`text-[10px] ${opt.color}`} data-testid={`result-badge-${result}`}>{opt.label}</Badge>;
}

const emptyForm = {
  product_id: "",
  lot_id: "",
  test_date: new Date().toISOString().slice(0, 10),
  lab_name: "Laboratoire indépendant",
  result: "EN_ATTENTE",
  document_url: "",
  notes: "",
};

export default function LabTests() {
  const confirm = useConfirm();
  const [tests, setTests] = useState([]);
  const [products, setProducts] = useState([]);
  const [lots, setLots] = useState([]);
  const [filterProduct, setFilterProduct] = useState("__all__");
  const [filterResult, setFilterResult] = useState("__all__");
  // Phase 101fa — Filtre par référence BL (via URL ?bl_ref=...) : matche les tests
  // dont les notes contiennent le tag « [BL <ref>] » injecté automatiquement.
  const [searchParams, setSearchParams] = useSearchParams();
  const blRefFilter = searchParams.get("bl_ref") || "";
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pdfFile, setPdfFile] = useState(null);  // Phase 83 — fichier PDF à uploader
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/lab-tests"),
      api.get("/products"),
      api.get("/lots"),
    ]);
    setTests(a.data);
    setProducts(b.data);
    setLots(c.data);
  };
  useEffect(() => { load(); }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  // Phase 87au — lotMap pour fallback de la date de fabrication (si snapshot manquant côté test legacy)
  const lotMap = useMemo(() => Object.fromEntries(lots.map((l) => [l.id, l])), [lots]);
  const lotsByProduct = useMemo(() => {
    const m = {};
    for (const l of lots) {
      if (!m[l.product_id]) m[l.product_id] = [];
      m[l.product_id].push(l);
    }
    return m;
  }, [lots]);

  const filtered = useMemo(() => {
    return tests.filter((t) => {
      if (filterProduct !== "__all__" && t.product_id !== filterProduct) return false;
      if (filterResult !== "__all__" && t.result !== filterResult) return false;
      if (blRefFilter && !(t.notes || "").includes(`[BL ${blRefFilter}]`)) return false;
      return true;
    });
  }, [tests, filterProduct, filterResult, blRefFilter]);

  const stats = useMemo(() => {
    const total = tests.length;
    const conforme = tests.filter((t) => t.result === "CONFORME").length;
    const nonConforme = tests.filter((t) => t.result === "NON_CONFORME").length;
    const enAttente = tests.filter((t) => t.result === "EN_ATTENTE").length;
    // Tests de ce mois
    const now = new Date();
    const mois = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const thisMonth = tests.filter((t) => (t.test_date || "").startsWith(mois)).length;
    return { total, conforme, nonConforme, enAttente, thisMonth };
  }, [tests]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, test_date: new Date().toISOString().slice(0, 10) });
    setPdfFile(null);
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      product_id: t.product_id,
      lot_id: t.lot_id || "",
      test_date: t.test_date,
      lab_name: t.lab_name || "Laboratoire indépendant",
      result: t.result,
      document_url: t.document_url || "",
      notes: t.notes || "",
    });
    setPdfFile(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.product_id) { toast.error("Sélectionnez un produit"); return; }
    setSaving(true);
    const payload = {
      product_id: form.product_id,
      lot_id: form.lot_id || null,
      test_date: form.test_date,
      lab_name: form.lab_name.trim() || "Laboratoire indépendant",
      result: form.result,
      document_url: form.document_url.trim(),
      notes: form.notes.trim(),
    };
    try {
      let testId;
      if (editing) {
        await api.put(`/lab-tests/${editing}`, payload);
        testId = editing;
        toast.success("Test mis à jour");
      } else {
        const { data } = await api.post("/lab-tests", payload);
        testId = data.id;
        toast.success("Test enregistré");
      }
      // Upload du PDF si l'utilisateur en a sélectionné un
      if (pdfFile && testId) {
        const fd = new FormData();
        fd.append("file", pdfFile);
        const { data: up } = await api.post(`/lab-tests/${testId}/document`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`PDF uploadé : ${up.filename} (${up.size_human})`);
      }
      setOpen(false);
      setPdfFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    const ok = await confirm({
      title: `Supprimer ce test ?`,
      description: `Test du ${fmtDate(t.test_date)} sur ${t.product_name}. Action irréversible.`,
      confirmText: "Supprimer",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/lab-tests/${t.id}`);
      toast.success("Test supprimé");
      setTests((prev) => prev.filter((x) => x.id !== t.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const availableLots = lotsByProduct[form.product_id] || [];

  return (
    <div className="space-y-6" data-testid="lab-tests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A] flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-[#1F4E3D]" /> Tests laboratoire
          </h1>
          <p className="text-sm text-[#575A57]">Analyses microbiologiques mensuelles · Traçabilité HACCP · Documents scannés.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-lab-test-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau test
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total, color: "text-[#1A1C1A]" },
          { label: "Ce mois-ci", value: stats.thisMonth, color: "text-[#1F4E3D]" },
          { label: "Conformes", value: stats.conforme, color: "text-[#1F4E3D]" },
          { label: "Non conformes", value: stats.nonConforme, color: "text-[#C05C47]" },
          { label: "En attente", value: stats.enAttente, color: "text-[#8C6B2F]" },
        ].map((s) => (
          <Card key={s.label} className="border-[#E5E5E0]">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#575A57] font-semibold">{s.label}</div>
              <div className={`text-2xl font-mono font-bold mt-1 ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>{s.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterProduct} onValueChange={setFilterProduct}>
              <SelectTrigger className="w-[240px] h-9" data-testid="filter-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous les produits</SelectItem>
                {sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterResult} onValueChange={setFilterResult}>
              <SelectTrigger className="w-[180px] h-9" data-testid="filter-result"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous résultats</SelectItem>
                {RESULT_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {blRefFilter && (
              <Badge
                variant="outline"
                className="bg-[#E7F0F5] border-[#95B8C9] text-[#164D63] gap-1 pl-2 pr-1 py-1"
                data-testid="filter-bl-ref-badge"
              >
                🔬 BL {blRefFilter}
                <button
                  type="button"
                  onClick={() => setSearchParams({})}
                  className="ml-1 hover:bg-[#95B8C9]/20 rounded p-0.5"
                  aria-label="Retirer le filtre BL"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="text-sm text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-6 text-center">
              {tests.length === 0 ? "Aucun test enregistré. Cliquez sur « Nouveau test » pour démarrer votre historique." : "Aucun test ne correspond aux filtres."}
            </div>
          ) : (
            <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F6] text-[10px] uppercase tracking-wider text-[#575A57]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Date remise labo</th>
                    <th className="text-left px-3 py-2 font-semibold">Date fab.</th>
                    <th className="text-left px-3 py-2 font-semibold">Produit</th>
                    <th className="text-left px-3 py-2 font-semibold">Lot</th>
                    <th className="text-left px-3 py-2 font-semibold">Laboratoire</th>
                    <th className="text-left px-3 py-2 font-semibold">Résultat</th>
                    <th className="text-left px-3 py-2 font-semibold">Document</th>
                    <th className="px-3 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const fabDate = t.production_date || lotMap[t.lot_id]?.production_date || null;
                    return (
                    <tr key={t.id} className="border-t border-[#F0F0EB] hover:bg-[#FAFAF7]" data-testid={`lab-test-row-${t.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{fmtDate(t.test_date)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-[#8C6B2F]" data-testid={`fab-date-${t.id}`}>
                        {fabDate ? fmtDate(fabDate) : <span className="text-[#A3A6A3]">—</span>}
                      </td>
                      <td className="px-3 py-2 font-medium text-[#1A1C1A]">
                        {t.product_name || productMap[t.product_id]?.name || "—"}
                        {t.notes && <div className="text-[11px] text-[#A3A6A3] italic mt-0.5 line-clamp-1">{t.notes}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#575A57] font-mono">{t.lot_number || "—"}</td>
                      <td className="px-3 py-2 text-xs text-[#575A57]">{t.lab_name}</td>
                      <td className="px-3 py-2">{resultBadge(t.result)}</td>
                      <td className="px-3 py-2">
                        {t.document_data || t.document_filename ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/api/lab-tests/${t.id}/document`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#1F4E3D] hover:underline flex items-center gap-1"
                            data-testid={`doc-link-${t.id}`}
                            title={t.document_filename || "PDF uploadé"}
                          >
                            <FileText className="w-3.5 h-3.5" /> Voir
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : t.document_url ? (
                          (() => {
                            const isHttp = /^https?:\/\//i.test(t.document_url);
                            if (isHttp) {
                              return (
                                <a
                                  href={t.document_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-[#1F4E3D] hover:underline flex items-center gap-1"
                                  data-testid={`doc-link-${t.id}`}
                                >
                                  <FileText className="w-3.5 h-3.5" /> Voir
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              );
                            }
                            // Phase 87aw — Chemin local (P:\…, /Volumes/…) :
                            // affichage en texte cliquable qui copie le chemin
                            // dans le presse-papier (pas de lien cassé).
                            return (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(t.document_url);
                                    toast.success("Chemin copié — collez-le dans l'explorateur de fichiers");
                                  } catch {
                                    toast.error("Impossible de copier — sélectionnez et copiez manuellement");
                                  }
                                }}
                                className="text-xs text-[#8C6B2F] hover:underline flex items-center gap-1 font-mono max-w-[220px] truncate"
                                title={`Cliquez pour copier : ${t.document_url}`}
                                data-testid={`doc-localpath-${t.id}`}
                              >
                                <FileText className="w-3.5 h-3.5 shrink-0" />
                                <span className="truncate">{t.document_url}</span>
                              </button>
                            );
                          })()
                        ) : (
                          <span className="text-xs text-[#A3A6A3] italic">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(t)} className="h-7 w-7" data-testid={`edit-lab-test-${t.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(t)} className="h-7 w-7 text-[#C05C47]" data-testid={`delete-lab-test-${t.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-[#1F4E3D]" />
              {editing ? "Modifier le test" : "Nouveau test laboratoire"}
            </DialogTitle>
            <DialogDescription>
              Enregistrez un résultat d'analyse microbiologique. Vous pouvez ajouter un lien vers le PDF scanné sur votre NAS.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-1.5 block">Produit *</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v, lot_id: "" })}>
                <SelectTrigger data-testid="form-product"><SelectValue placeholder="Sélectionner un produit…" /></SelectTrigger>
                <SelectContent>
                  {sortByName(products).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">Lot spécifique (optionnel)</Label>
              <Select value={form.lot_id || "__none__"} onValueChange={(v) => setForm({ ...form, lot_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="form-lot"><SelectValue placeholder="Aucun lot précis" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Test générique (sans lot) —</SelectItem>
                  {availableLots.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.lot_number}{l.dlc ? ` · DLC ${l.dlc}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.product_id && availableLots.length === 0 && (
                <p className="text-[11px] text-[#A3A6A3] italic mt-1">Aucun lot actif pour ce produit. Le test sera générique.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Date du test *</Label>
                <Input type="date" value={form.test_date} onChange={(e) => setForm({ ...form, test_date: e.target.value })} data-testid="form-test-date" />
              </div>
              <div>
                <Label className="mb-1.5 block">Résultat *</Label>
                <Select value={form.result} onValueChange={(v) => setForm({ ...form, result: v })}>
                  <SelectTrigger data-testid="form-result"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESULT_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block flex items-center gap-1.5"><Building className="w-3.5 h-3.5" /> Laboratoire</Label>
              <Input value={form.lab_name} onChange={(e) => setForm({ ...form, lab_name: e.target.value })} placeholder="Laboratoire indépendant" data-testid="form-lab-name" />
            </div>
            <div>
              <Label className="mb-1.5 block flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Document PDF du rapport</Label>
              <div className="space-y-3">
                {/* Phase 83 — Upload direct du PDF dans AgroManage */}
                <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-3">
                  <Label className="text-[11px] uppercase tracking-wider text-[#8C6B2F] mb-1.5 block font-semibold">
                    📎 Uploader le PDF (recommandé)
                  </Label>
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#1F4E3D] file:text-white file:cursor-pointer hover:file:bg-[#173B2E] cursor-pointer"
                    data-testid="form-pdf-file"
                  />
                  {pdfFile && (
                    <div className="text-[11px] text-[#1F4E3D] mt-1.5 font-medium">
                      ✓ {pdfFile.name} — {(pdfFile.size / 1024).toFixed(0)} KB
                    </div>
                  )}
                  {editing && (
                    <p className="text-[10px] text-[#8C6B2F]/80 italic mt-2">
                      Sélectionnez un nouveau PDF pour remplacer celui actuel (le précédent est écrasé).
                    </p>
                  )}
                  <p className="text-[10px] text-[#8C6B2F]/80 italic mt-1">
                    Max 10 MB. Le PDF est stocké dans AgroManage et toujours accessible
                    depuis n'importe où (pas de dépendance NAS/disque externe).
                  </p>
                </div>

                {/* Lien externe / chemin local optionnel (rétrocompatibilité)
                    Phase 87aw — Type "text" (non "url") pour accepter les chemins
                    locaux Windows / pCloud type "P:\Labo\…\rapport.pdf" sans
                    blocage de validation HTML. */}
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-[#575A57] mb-1 block font-semibold">
                    ⛓ Ou un lien externe / chemin local (NAS, drive cloud, P:\…)
                  </Label>
                  <Input
                    type="text"
                    value={form.document_url}
                    onChange={(e) => setForm({ ...form, document_url: e.target.value })}
                    placeholder="https://drive.google.com/… ou P:\Labo\2026\rapport.pdf"
                    data-testid="form-document-url"
                  />
                  <p className="text-[10px] text-[#A3A6A3] italic mt-1">
                    Si vous uploadez un PDF ci-dessus, le lien externe sert de fallback uniquement.
                    Les chemins locaux (P:\…) sont stockés en texte copiable.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes (optionnel)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Observations particulières, recommandations du laboratoire…" data-testid="form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={submit} disabled={saving || !form.product_id} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="form-submit">
              {editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
